import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from 'rehype-sanitize';

import { useAuth } from '../../hooks/useAuth';
import { ORDERS_QUERY } from '../../queries/orderQueries';
import { RETRY_AUDIENCE_CRAWL } from '../../mutations/audienceMutations';
import {
  buildReportList,
  hasGeneratingReports as listHasGeneratingReports,
} from './reportStatus';
import type { ReportPkg } from './reportStatus';
import {
  START_ASK_ADDY_CONVERSATION,
  ASK_ADDY_CONVERSATION_QUERY,
  ASK_ADDY_OUTPUT_URL_QUERY,
  streamAskAddyMessage,
} from '../../queries/askAddy';
import './askAddy.css';

// ── Mockup palette (inline so this view doesn't depend on the Tailwind theme) ──
const C = {
  teal: '#3bbfa0',
  tealDark: '#2da88b',
  tealLight: '#e8f8f3',
  purpleBg: '#f3f0ff',
  purpleText: '#5b47b0',
  coralBg: '#fef0ec',
  coralText: '#c44b2b',
  blueBg: '#eef4ff',
  blueText: '#2563a0',
  indigoBg: '#eef0fb',
  indigoText: '#3b3f9e',
  textPrimary: '#1a1d2e',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  border: '#e5e7eb',
  borderLight: '#f0f1f3',
  bodyBg: '#f3f4f6',
  cardBg: '#ffffff',
  chatAiBg: '#f8f9fb',
};

// Render Addy's raw markdown output (headings, bold, lists, tables) using the
// already-bundled @uiw preview. Scoped `.addy-md` overrides (askAddy.css) tame its
// GitHub-style defaults to the compact chat look; rehype-sanitize keeps it XSS-safe.
function MarkdownView({ source, fontSize = 13 }: { source: string; fontSize?: number }) {
  return (
    <div data-color-mode="light" className="addy-md">
      <MDEditor.Markdown
        source={source}
        rehypePlugins={[[rehypeSanitize]]}
        style={{ background: 'transparent', fontSize }}
      />
    </div>
  );
}

// Small inline teal loading ring (rotation keyframes live in askAddy.css).
function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      className="addy-spin"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        flexShrink: 0,
        border: `2px solid ${C.border}`,
        borderTopColor: C.teal,
        borderRadius: '50%',
      }}
    />
  );
}

type PromptType =
  | 'talking_points'
  | 'client_email'
  | 'exec_summary'
  | 'audience_recs'
  | 'cog_sci_playbook';
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = new Date(isNaN(Number(v)) ? v : Number(v));
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const Svg = ({
  children,
  size = 20,
  stroke = 2,
}: {
  children: ReactNode;
  size?: number;
  stroke?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const AddyIcon = ({ size = 32 }: { size?: number }) => (
  <Svg size={size} stroke={1.8}>
    <path d="M12 2a7 7 0 017 7c0 3-2 5.5-4 7l-1 2h-4l-1-2c-2-1.5-4-4-4-7a7 7 0 017-7z" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </Svg>
);

const ReportIcon = ({ size = 22 }: { size?: number }) => (
  <Svg size={size} stroke={1.8}>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </Svg>
);

// Deliberately not the Spinner: a spinner is what communicates "in progress",
// and a permanently failed report showing one is the bug this replaces.
const FailedReportIcon = ({ size = 22 }: { size?: number }) => (
  <Svg size={size} stroke={1.8}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="13" />
    <line x1="12" y1="16" x2="12" y2="16" />
  </Svg>
);

interface Action {
  type: PromptType;
  title: string;
  badge: string;
  desc: string;
  footer: string;
  enabled: boolean;
  accent: string;
  iconBg: string;
  iconColor: string;
  badgeBg: string;
  badgeColor: string;
  icon: ReactNode;
}

// Order + colors mirror the mockup. Only Talking Points is wired today;
// the others need PDF render / engagement inputs / web search (Slices 4–6).
const ACTIONS: Action[] = [
  {
    type: 'exec_summary',
    title: 'Executive summary',
    badge: 'Executive summary',
    desc: 'AI-written narrative of the audience profile, ready to share with clients.',
    footer: 'Downloads as .pdf',
    enabled: true,
    accent: C.teal,
    iconBg: C.tealLight,
    iconColor: '#1a7a63',
    badgeBg: C.tealLight,
    badgeColor: '#1a7a63',
    icon: (
      <Svg size={17}>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </Svg>
    ),
  },
  {
    type: 'talking_points',
    title: 'Top talking points',
    badge: 'Top talking points',
    desc: 'The most compelling, differentiated data points to guide your client conversations.',
    footer: 'Copy to clipboard',
    enabled: true,
    accent: C.purpleText,
    iconBg: C.purpleBg,
    iconColor: C.purpleText,
    badgeBg: C.purpleBg,
    badgeColor: C.purpleText,
    icon: (
      <Svg size={17}>
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </Svg>
    ),
  },
  {
    type: 'client_email',
    title: 'Sales email',
    badge: 'Sales email',
    desc: 'Client-facing email summarizing key findings from the profile report.',
    footer: 'Copy to clipboard',
    enabled: true,
    accent: C.coralText,
    iconBg: C.coralBg,
    iconColor: C.coralText,
    badgeBg: C.coralBg,
    badgeColor: C.coralText,
    icon: (
      <Svg size={17}>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </Svg>
    ),
  },
  {
    type: 'audience_recs',
    title: 'Audience recommendations',
    badge: 'Audience strategy',
    desc: 'Data variables to explore based on goals, market context and competitor intel.',
    footer: 'Downloads as .pdf',
    enabled: true,
    accent: C.blueText,
    iconBg: C.blueBg,
    iconColor: C.blueText,
    badgeBg: C.blueBg,
    badgeColor: C.blueText,
    icon: (
      <Svg size={17}>
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" />
        <line x1="9" y1="8" x2="15" y2="8" />
        <line x1="17" y1="16" x2="23" y2="16" />
      </Svg>
    ),
  },
  {
    type: 'cog_sci_playbook',
    title: 'Cognitive science playbook',
    badge: 'Cognitive science',
    desc: 'Why this audience buys, read through Alliant’s consumer research library.',
    footer: 'Downloads as .pdf',
    enabled: true,
    accent: C.indigoText,
    iconBg: C.indigoBg,
    iconColor: C.indigoText,
    badgeBg: C.indigoBg,
    badgeColor: C.indigoText,
    // The same brain mark the playbook PDF uses on its cognitive panel, so the
    // card reads as the deliverable it produces.
    icon: (
      <Svg size={17}>
        <path d="M12 3a4 4 0 00-4 4 3 3 0 00-1 5.8V17a3 3 0 006 0V3z" />
        <path d="M12 3a4 4 0 014 4 3 3 0 011 5.8V17a3 3 0 01-6 0" />
      </Svg>
    ),
  },
];

// Client Email engagement questions — the four the Client_Email_Generator_Prompt
// requires answered before drafting (presented in-platform as tappable options).
// `key` becomes a field on the `inputs` object the server folds into the prompt.
interface EngagementQuestion {
  key: string;
  question: string;
  options: string[];
}
const CLIENT_EMAIL_QUESTIONS: EngagementQuestion[] = [
  {
    key: 'relationship',
    question: 'Is this a current client or a prospect?',
    options: ['Current client', 'Prospect'],
  },
  {
    key: 'dataSource',
    question:
      'Is the audience built from Alliant data, or the client’s own data with Alliant insights overlaid?',
    options: ['Alliant data', 'Client data + Alliant overlay'],
  },
  {
    key: 'partnerType',
    question: 'Is the client/prospect a brand, an agency, or a technology partner?',
    options: ['Brand', 'Agency', 'Technology partner'],
  },
  {
    key: 'cadence',
    question:
      'Have you spoken with them recently, or is this a re-engagement after a quiet period?',
    options: ['Recent contact', 'Re-engagement after a gap'],
  },
];

// Audience Recommendations engagement — the five free-text context inputs the
// Audience_Recommendations_Prompt requires before generating. Each `key` becomes
// a field on the `inputs` object the server folds into the prompt + drives the
// templated market-research searches.
interface RecsField {
  key: string;
  label: string;
  placeholder: string;
  multiline?: boolean;
}
const AUDIENCE_RECS_FIELDS: RecsField[] = [
  { key: 'brand', label: 'Brand name', placeholder: 'e.g. Nike' },
  {
    key: 'industry',
    label: 'Industry',
    placeholder: 'e.g. athletic footwear and apparel',
  },
  {
    key: 'audienceDescription',
    label: 'Audience description',
    placeholder:
      'Who this audience is and what you built it for (e.g. Nike’s new kids shoewear collab launching on TikTok)',
    multiline: true,
  },
  {
    key: 'campaign',
    label: 'Campaign purpose, objective, and channels',
    placeholder:
      'e.g. Awareness with downstream conversion, TikTok primary, Meta secondary',
    multiline: true,
  },
  {
    key: 'competitors',
    label: 'Top 2 to 3 competitors',
    placeholder: 'e.g. Adidas, Puma, New Balance',
  },
];

function AiRow({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, maxWidth: '85%', alignSelf: 'flex-start' }}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 10,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          background: `linear-gradient(135deg, ${C.teal}, #2a9d8f)`,
          color: '#fff',
        }}
      >
        A
      </div>
      <div
        style={{
          padding: '12px 16px',
          borderRadius: 14,
          borderBottomLeftRadius: 4,
          fontSize: 14,
          lineHeight: 1.6,
          background: C.chatAiBg,
          border: `1px solid ${C.borderLight}`,
          color: C.textPrimary,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function UserRow({ initial, children }: { initial: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        maxWidth: '85%',
        alignSelf: 'flex-end',
        flexDirection: 'row-reverse',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 10,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          background: '#e0e3e8',
          color: C.textSecondary,
        }}
      >
        {initial}
      </div>
      <div
        style={{
          padding: '12px 16px',
          borderRadius: 14,
          borderBottomRightRadius: 4,
          fontSize: 14,
          lineHeight: 1.6,
          background: C.teal,
          color: '#fff',
          whiteSpace: 'pre-wrap',
        }}
      >
        {children}
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: C.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  marginBottom: 8,
};

export default function AskAddy() {
  const { packageId: routePackageId } = useParams();
  // ?prompt=<type> (set by the My Work → Ask Addy "Open" link) re-opens a past
  // request: the prompt is auto-started below so its conversation is restored.
  const [searchParams] = useSearchParams();
  const promptParam = searchParams.get('prompt') as PromptType | null;
  const { user } = useAuth();
  const userInitial = (
    ((user as any)?.firstName ||
      (user as any)?.name ||
      (user as any)?.email ||
      'Y') as string
  )
    .charAt(0)
    .toUpperCase();

  // Profile reports for the dropdown — Insight / Audience-profile orders only.
  const {
    data: ordersData,
    loading: reportsLoading,
    error: reportsError,
    startPolling: startReportsPolling,
    stopPolling: stopReportsPolling,
    refetch: refetchReports,
  } = useQuery(ORDERS_QUERY);
  const reports: ReportPkg[] = buildReportList(
    (ordersData?.orders || []) as any[]
  );

  // While any report is still generating, poll so it flips to ready without
  // a manual refresh (report generation takes a few minutes).
  //
  // Terminal states must NOT keep this alive: with the old `!r.ready` test, a
  // list containing a permanently failed report polled every 30s until the
  // user navigated away.
  const hasGeneratingReports = listHasGeneratingReports(reports);
  useEffect(() => {
    if (!hasGeneratingReports) return;
    startReportsPolling(30_000);
    return () => stopReportsPolling();
  }, [hasGeneratingReports, startReportsPolling, stopReportsPolling]);

  // The selected report drives everything. A `:packageId` in the URL pre-selects.
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    routePackageId ?? null
  );
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const packageId = selectedPackageId;
  const selectedReport = reports.find((r) => r.id === packageId);
  // A URL-preselected report can still be generating — or have failed
  // outright; block actions in both cases. Unknown (report list not loaded
  // yet) counts as ready.
  const selectedReportReady = selectedReport
    ? selectedReport.status === 'ready'
    : true;

  // Recovering a failed report means fixing the audience it was built from —
  // the retry re-runs the export, wakes the crawler, and re-queues the report.
  const [retryAudienceCrawl, { loading: retryingCrawl, error: retryCrawlError }] =
    useMutation(RETRY_AUDIENCE_CRAWL);
  const [retryRequested, setRetryRequested] = useState(false);
  const canRetrySelected =
    !!selectedReport &&
    selectedReport.status === 'failed' &&
    selectedReport.audienceCrawlFailed &&
    !!selectedReport.audienceId;

  const handleRetrySelected = async () => {
    if (!selectedReport?.audienceId) return;
    try {
      await retryAudienceCrawl({
        variables: { audienceId: selectedReport.audienceId },
      });
      setRetryRequested(true);
      // The export is scheduled, not immediate, so the report flips back to
      // generating a moment later. One delayed refetch rather than a new poll:
      // if the report did NOT come back, nothing here starts spinning again —
      // the generating-only poll above owns that from then on.
      setTimeout(() => {
        refetchReports().catch(() => {});
      }, 5_000);
    } catch {
      // Surfaced from retryCrawlError below.
    }
  };

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptType | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Client Email only: the inline engagement step gathers the 4 prompt answers
  // BEFORE generating. `engagementOpen` toggles that step; `engagementAnswers`
  // collects the tapped options keyed by question.key.
  const [engagementOpen, setEngagementOpen] = useState(false);
  const [engagementAnswers, setEngagementAnswers] = useState<Record<string, string>>(
    {}
  );
  // Audience Recommendations only: the inline 5-input step gathers the campaign
  // context BEFORE generating. `recsOpen` toggles that step; `recsAnswers`
  // collects the typed values keyed by field.key.
  const [recsOpen, setRecsOpen] = useState(false);
  const [recsAnswers, setRecsAnswers] = useState<Record<string, string>>({});
  // Regenerate composer: lets the user describe changes before a fresh
  // generation. `refineOpen` toggles the modal; `refineText` holds the typed
  // changes (empty = a plain fresh regenerate).
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineText, setRefineText] = useState('');
  // Exec Summary download state (presigned URL fetched lazily on click).
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const abortRef = useRef<null | (() => void)>(null);
  const seededRef = useRef(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const apollo = useApolloClient();
  const [startConversation, { loading: starting, error: startError }] = useMutation(
    START_ASK_ADDY_CONVERSATION
  );

  const { data, startPolling, stopPolling } = useQuery(ASK_ADDY_CONVERSATION_QUERY, {
    variables: { id: conversationId },
    skip: !conversationId,
    fetchPolicy: 'network-only',
  });

  const conversation = data?.askAddyConversation;
  const status: string | undefined = conversation?.status;
  const activeAction = ACTIONS.find((a) => a.type === selectedPrompt);

  // The first PDF output (if any) on the active conversation — drives the
  // Download PDF button in the output card (exec_summary today).
  const pdfOutput: { kind?: string; s3Key?: string } | undefined = (
    (conversation?.outputs || []) as any[]
  ).find((o) => o?.s3Key && String(o.kind || '').includes('pdf'));

  // Poll until the first output is ready (or fails).
  useEffect(() => {
    if (!conversationId) return undefined;
    if (status === 'ready' || status === 'failed') stopPolling();
    else startPolling(3000);
    return () => stopPolling();
  }, [conversationId, status, startPolling, stopPolling]);

  // Seed the chat once ready. Restore the FULL prior conversation so a reused
  // (cached) conversation brings back its chat history: messages[0] is the
  // assistant's output card; any subsequent turns render as bubbles below it.
  useEffect(() => {
    if (status === 'ready' && conversation && !seededRef.current) {
      const all = (conversation.messages || []) as ChatMessage[];
      // The output card requires messages[0] to be the assistant output. If the
      // stored history happens to start with something else, fall back to the
      // first assistant message so the card still renders.
      let seed: ChatMessage[] = all.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      if (seed.length && seed[0].role !== 'assistant') {
        const firstAssistantIdx = seed.findIndex((m) => m.role === 'assistant');
        if (firstAssistantIdx > 0) {
          seed = [
            seed[firstAssistantIdx],
            ...seed.slice(0, firstAssistantIdx),
            ...seed.slice(firstAssistantIdx + 1),
          ];
        }
      }
      if (seed.length) {
        setMessages(seed);
        seededRef.current = true;
      }
    }
  }, [status, conversation]);

  // Keep the chat scrolled to the latest.
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [messages, status]);

  useEffect(() => () => abortRef.current?.(), []);

  // `force` is passed (true) by Regenerate to bypass the server-side reuse of a
  // prior ready conversation; normal card clicks omit it (reuse when possible).
  // `inputs` carries prompt-specific engagement answers (Client Email).
  const startPrompt = async (
    promptType: PromptType,
    force = false,
    inputs?: Record<string, string>
  ) => {
    if (!packageId) return;
    setSelectedPrompt(promptType);
    seededRef.current = false;
    setMessages([]);
    setChatError(null);
    setDownloadError(null);
    setEngagementOpen(false);
    setRecsOpen(false);
    setRefineOpen(false);
    try {
      const res = await startConversation({
        variables: { packageId, promptType, force, inputs },
      });
      const id = res.data?.startAskAddyConversation?.id;
      if (id) setConversationId(id);
    } catch {
      // surfaced via startError
    }
  };

  // Re-open a past request from My Work: when a `?prompt=<type>` param is
  // present, auto-start that prompt once. startPrompt reuses the existing ready
  // conversation for { user, package, promptType } (idempotent, no new model
  // charge), and the seeding effect above restores its output card + chat.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!routePackageId || !promptParam) return;
    if (!ACTIONS.some((a) => a.type === promptParam)) return;
    autoStartedRef.current = true;
    startPrompt(promptParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePackageId, promptParam]);

  // Card click. Client Email and Audience Recommendations need their inputs
  // first, so they open the inline step instead of generating immediately; the
  // others start right away (Talking Points, Exec Summary, and the Cognitive
  // Science Playbook take no pre-inputs).
  const chooseAction = (promptType: PromptType) => {
    if (promptType === 'client_email') {
      setSelectedPrompt(promptType);
      setEngagementAnswers({});
      setEngagementOpen(true);
      return;
    }
    if (promptType === 'audience_recs') {
      setSelectedPrompt(promptType);
      setRecsAnswers({});
      setRecsOpen(true);
      return;
    }
    startPrompt(promptType);
  };

  // Regenerate the current output (force a fresh model run). Reuses the
  // conversation's stored engagement answers so a Client Email regenerate keeps
  // the same context instead of re-asking; other prompts pass no inputs.
  // Regenerate now asks what to change first: open the composer (empty), then
  // confirmRegenerate runs the forced regeneration with any typed changes.
  const regenerate = () => {
    if (!selectedPrompt) return;
    setRefineText('');
    setRefineOpen(true);
  };

  const confirmRegenerate = () => {
    if (!selectedPrompt) return;
    // Carry the conversation's persistent context forward, but drop any prior
    // one-shot `adjustments` so each regeneration applies only what's typed now.
    const base = { ...((conversation?.inputs || {}) as Record<string, string>) };
    delete base.adjustments;
    const changes = refineText.trim();
    const inputs: Record<string, string> = changes
      ? { ...base, adjustments: changes }
      : base;
    setRefineOpen(false);
    startPrompt(
      selectedPrompt,
      true,
      Object.keys(inputs).length ? inputs : undefined
    );
  };

  const engagementComplete = CLIENT_EMAIL_QUESTIONS.every(
    (q) => engagementAnswers[q.key]
  );

  const submitEngagement = () => {
    if (!engagementComplete) return;
    startPrompt('client_email', false, engagementAnswers);
  };

  const recsComplete = AUDIENCE_RECS_FIELDS.every((f) =>
    (recsAnswers[f.key] || '').trim()
  );

  const submitRecs = () => {
    if (!recsComplete) return;
    startPrompt('audience_recs', false, recsAnswers);
  };

  const backToActions = () => {
    abortRef.current?.();
    setConversationId(null);
    setSelectedPrompt(null);
    setMessages([]);
    setChatError(null);
    setEngagementOpen(false);
    setEngagementAnswers({});
    setRecsOpen(false);
    setRecsAnswers({});
    setRefineOpen(false);
    setRefineText('');
    setDownloadError(null);
    seededRef.current = false;
  };

  // Fetch a fresh presigned URL for the conversation's PDF output and open it.
  // Lazy (no-cache) so the short-lived link is valid each click.
  const downloadOutput = async () => {
    if (!conversationId || !pdfOutput) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await apollo.query({
        query: ASK_ADDY_OUTPUT_URL_QUERY,
        variables: { conversationId, kind: pdfOutput.kind },
        fetchPolicy: 'no-cache',
      });
      const link = res.data?.askAddyOutputUrl?.downloadLink;
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        setDownloadError('Could not get a download link.');
      }
    } catch (err) {
      setDownloadError((err as Error).message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || !conversationId || streaming || status !== 'ready') return;
    setChatError(null);
    setInput('');
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '' },
    ]);
    setStreaming(true);

    abortRef.current = streamAskAddyMessage(
      conversationId,
      text,
      {
        onDelta: (delta: string) =>
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, content: last.content + delta };
            }
            return next;
          }),
        onDone: () => setStreaming(false),
        onError: (msg: string) => {
          setChatError(msg);
          setStreaming(false);
        },
      },
      (user as any)?.token
    );
  };

  const copyOutput = async () => {
    const first = messages.find((m) => m.role === 'assistant');
    if (!first) return;
    try {
      await navigator.clipboard.writeText(first.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable; ignore
    }
  };

  // ── Audience Recs step: collect the 5 context inputs before generating ───
  if (recsOpen && !conversationId) {
    return (
      <div
        style={{
          background: C.bodyBg,
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 24px',
          color: C.textPrimary,
        }}
      >
        <div style={{ width: '100%', maxWidth: 640 }}>
          <button
            type="button"
            onClick={() => {
              setRecsOpen(false);
              setSelectedPrompt(null);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              color: C.textSecondary,
              fontSize: 13,
              cursor: 'pointer',
              padding: 0,
              marginBottom: 16,
              font: 'inherit',
            }}
          >
            <Svg size={15}>
              <polyline points="15 18 9 12 15 6" />
            </Svg>
            Back
          </button>

          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
            Tell me about your campaign
          </div>
          <div
            style={{
              fontSize: 14,
              color: C.textSecondary,
              lineHeight: 1.5,
              marginBottom: 24,
            }}
          >
            Addy combines this with live market research and Alliant&apos;s data
            taxonomy to recommend audiences worth testing.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {AUDIENCE_RECS_FIELDS.map((f, i) => {
              const value = recsAnswers[f.key] || '';
              const setValue = (v: string) =>
                setRecsAnswers((prev) => ({ ...prev, [f.key]: v }));
              const fieldStyle: React.CSSProperties = {
                width: '100%',
                padding: '10px 14px',
                borderRadius: 10,
                border: `1.5px solid ${value.trim() ? C.blueText : C.border}`,
                fontSize: 14,
                outline: 'none',
                background: C.cardBg,
                color: C.textPrimary,
                font: 'inherit',
              };
              return (
                <div key={f.key}>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: C.textPrimary,
                      marginBottom: 8,
                    }}
                  >
                    {i + 1}. {f.label}
                  </div>
                  {f.multiline ? (
                    <textarea
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder={f.placeholder}
                      rows={2}
                      style={{ ...fieldStyle, resize: 'vertical' }}
                    />
                  ) : (
                    <input
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder={f.placeholder}
                      style={fieldStyle}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            disabled={!recsComplete || starting}
            onClick={submitRecs}
            style={{
              marginTop: 28,
              width: '100%',
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: recsComplete && !starting ? 'pointer' : 'default',
              background: C.teal,
              color: '#fff',
              opacity: recsComplete && !starting ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              font: 'inherit',
            }}
          >
            {starting ? (
              <>
                <Spinner size={14} />
                Building your recommendations…
              </>
            ) : (
              'Generate recommendations'
            )}
          </button>
          {startError && (
            <div style={{ marginTop: 12, color: C.coralText, fontSize: 13 }}>
              {startError.message}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Client Email engagement step: collect the 4 answers before generating ─
  if (engagementOpen && !conversationId) {
    return (
      <div
        style={{
          background: C.bodyBg,
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 24px',
          color: C.textPrimary,
        }}
      >
        <div style={{ width: '100%', maxWidth: 640 }}>
          <button
            type="button"
            onClick={() => {
              setEngagementOpen(false);
              setSelectedPrompt(null);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              color: C.textSecondary,
              fontSize: 13,
              cursor: 'pointer',
              padding: 0,
              marginBottom: 16,
              font: 'inherit',
            }}
          >
            <Svg size={15}>
              <polyline points="15 18 9 12 15 6" />
            </Svg>
            Back
          </button>

          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
            A few quick questions
          </div>
          <div
            style={{
              fontSize: 14,
              color: C.textSecondary,
              lineHeight: 1.5,
              marginBottom: 24,
            }}
          >
            These shape the tone and framing of your outreach email.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {CLIENT_EMAIL_QUESTIONS.map((q, i) => (
              <div key={q.key}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: C.textPrimary,
                    marginBottom: 10,
                  }}
                >
                  {i + 1}. {q.question}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {q.options.map((opt) => {
                    const selected = engagementAnswers[q.key] === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setEngagementAnswers((prev) => ({ ...prev, [q.key]: opt }))
                        }
                        style={{
                          padding: '9px 16px',
                          borderRadius: 22,
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: 'pointer',
                          border: `1.5px solid ${selected ? C.coralText : C.border}`,
                          background: selected ? C.coralBg : C.cardBg,
                          color: selected ? C.coralText : C.textSecondary,
                          font: 'inherit',
                        }}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={!engagementComplete || starting}
            onClick={submitEngagement}
            style={{
              marginTop: 28,
              width: '100%',
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: engagementComplete && !starting ? 'pointer' : 'default',
              background: C.teal,
              color: '#fff',
              opacity: engagementComplete && !starting ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              font: 'inherit',
            }}
          >
            {starting ? (
              <>
                <Spinner size={14} />
                Drafting your email…
              </>
            ) : (
              'Generate email'
            )}
          </button>
          {startError && (
            <div style={{ marginTop: 12, color: C.coralText, fontSize: 13 }}>
              {startError.message}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Home: choose a report (dropdown) + pick an action ───────────────────
  if (!conversationId) {
    return (
      <div
        style={{
          background: C.bodyBg,
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          color: C.textPrimary,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 20,
              background: `linear-gradient(135deg, ${C.teal}, #2a9d8f)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 32px rgba(59,191,160,0.2)',
              color: '#fff',
            }}
          >
            <AddyIcon size={32} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 600 }}>Ask Addy</div>
          <div
            style={{
              fontSize: 15,
              color: C.textSecondary,
              maxWidth: 440,
              lineHeight: 1.55,
              margin: '6px auto 0',
            }}
          >
            Select a profile report, then choose an action. Addy generates a first output
            you can refine in chat.
          </div>
        </div>

        {/* 1. Report selector (dropdown) */}
        <div style={{ width: '100%', maxWidth: 640, marginBottom: 24, position: 'relative' }}>
          <div style={labelStyle}>1. Select a profile report</div>
          <button
            type="button"
            onClick={() => setReportMenuOpen((o) => !o)}
            style={{
              width: '100%',
              textAlign: 'left',
              background: C.cardBg,
              border: `1.5px solid ${packageId ? C.teal : C.border}`,
              borderRadius: 12,
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            <span
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: C.tealLight,
                color: C.tealDark,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {reportsLoading && !selectedReport ? (
                <Spinner size={20} />
              ) : selectedReport?.status === 'failed' ? (
                <FailedReportIcon size={22} />
              ) : (
                <ReportIcon size={22} />
              )}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {selectedReport ? (
                <>
                  <span
                    style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.textPrimary }}
                  >
                    {selectedReport.name || 'Profile report'}
                  </span>
                  {selectedReport.status === 'failed' ? (
                    <span
                      style={{ display: 'block', fontSize: 12, color: C.coralText, marginTop: 2 }}
                    >
                      Couldn't be generated
                    </span>
                  ) : selectedReport.status === 'generating' ? (
                    <span
                      style={{ display: 'block', fontSize: 12, color: C.textSecondary, marginTop: 2 }}
                    >
                      Generating — this takes a few minutes…
                    </span>
                  ) : (
                    fmtDate(selectedReport.purchasedAt) && (
                      <span
                        style={{ display: 'block', fontSize: 12, color: C.textSecondary, marginTop: 2 }}
                      >
                        Generated {fmtDate(selectedReport.purchasedAt)}
                      </span>
                    )
                  )}
                </>
              ) : (
                <span style={{ fontSize: 14, color: C.textMuted }}>
                  {reportsLoading ? 'Loading your reports…' : 'Choose a profile report…'}
                </span>
              )}
            </span>
            <span
              style={{
                color: C.textMuted,
                fontSize: 18,
                transform: reportMenuOpen ? 'rotate(90deg)' : 'none',
                transition: 'transform .15s',
              }}
            >
              {'›'}
            </span>
          </button>

          {/* A failed report is a dead end without this: the actionable remedy
              lives on the audience it was built from, not on the report. */}
          {selectedReport?.status === 'failed' && (
            <div
              style={{
                marginTop: 10,
                padding: '12px 14px',
                background: C.coralBg,
                borderRadius: 10,
                fontSize: 13,
                color: C.coralText,
                lineHeight: 1.5,
              }}
            >
              {canRetrySelected ? (
                <>
                  <div>
                    Preparing this report's audience didn't finish, so the
                    report couldn't be generated.
                  </div>
                  <button
                    type="button"
                    onClick={handleRetrySelected}
                    disabled={retryingCrawl || retryRequested}
                    style={{
                      marginTop: 10,
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: `1px solid ${C.coralText}`,
                      background: retryRequested ? 'transparent' : C.cardBg,
                      color: C.coralText,
                      cursor:
                        retryingCrawl || retryRequested ? 'default' : 'pointer',
                      opacity: retryingCrawl || retryRequested ? 0.6 : 1,
                      font: 'inherit',
                      fontWeight: 600,
                    }}
                  >
                    {retryingCrawl
                      ? 'Starting…'
                      : retryRequested
                        ? 'Retry started'
                        : 'Retry and regenerate'}
                  </button>
                  {retryRequested && (
                    <div style={{ marginTop: 8 }}>
                      Preparing the audience again — the report regenerates on
                      its own once that finishes, which takes a few minutes.
                    </div>
                  )}
                </>
              ) : (
                <div>
                  This report couldn't be generated. Check the audience in My
                  Work, or order the report again.
                </div>
              )}
              {retryCrawlError && (
                <div style={{ marginTop: 8 }}>{retryCrawlError.message}</div>
              )}
            </div>
          )}

          {reportMenuOpen && (
            <>
              {/* click-away */}
              <div
                onClick={() => setReportMenuOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  right: 0,
                  zIndex: 50,
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  maxHeight: 320,
                  overflowY: 'auto',
                  padding: 8,
                }}
              >
                {reportsLoading && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: 12,
                      color: C.textSecondary,
                      fontSize: 13,
                    }}
                  >
                    <Spinner size={14} />
                    Loading your reports…
                  </div>
                )}
                {reportsError && (
                  <div style={{ padding: 12, color: C.coralText, fontSize: 13 }}>
                    {reportsError.message}
                  </div>
                )}
                {!reportsError && !reportsLoading && reports.length === 0 && (
                  <div style={{ padding: 12, color: C.textSecondary, fontSize: 13 }}>
                    No profile reports yet. Generate one from Insights.
                  </div>
                )}
                {reports.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    // A failed report stays selectable — that is how the user
                    // reaches the reason and the retry. The action cards are
                    // gated separately, on selectedReportReady.
                    disabled={r.status === 'generating'}
                    onClick={() => {
                      if (r.status === 'generating') return;
                      setSelectedPackageId(r.id);
                      setRetryRequested(false);
                      setReportMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      borderRadius: 10,
                      border: 'none',
                      background: r.id === packageId ? C.tealLight : 'transparent',
                      cursor: r.status === 'generating' ? 'default' : 'pointer',
                      opacity: r.status === 'generating' ? 0.45 : 1,
                      font: 'inherit',
                    }}
                  >
                    <span
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background:
                          r.status === 'failed' ? C.coralBg : C.tealLight,
                        color:
                          r.status === 'failed' ? C.coralText : C.tealDark,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {r.status === 'ready' ? (
                        <ReportIcon size={18} />
                      ) : r.status === 'failed' ? (
                        <FailedReportIcon size={18} />
                      ) : (
                        <Spinner size={16} />
                      )}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{ display: 'block', fontSize: 14, fontWeight: 500, color: C.textPrimary }}
                      >
                        {r.name || 'Profile report'}
                      </span>
                      {r.status === 'failed' ? (
                        <span
                          style={{ display: 'block', fontSize: 11, color: C.coralText, marginTop: 1 }}
                        >
                          Couldn't be generated
                        </span>
                      ) : r.status === 'generating' ? (
                        <span
                          style={{ display: 'block', fontSize: 11, color: C.textSecondary, marginTop: 1 }}
                        >
                          Generating — this takes a few minutes…
                        </span>
                      ) : (
                        fmtDate(r.purchasedAt) && (
                          <span
                            style={{ display: 'block', fontSize: 11, color: C.textSecondary, marginTop: 1 }}
                          >
                            Generated {fmtDate(r.purchasedAt)}
                          </span>
                        )
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* 2. Action cards (disabled until a report is chosen) */}
        <div style={{ width: '100%', maxWidth: 640 }}>
          <div style={labelStyle}>2. What would you like to do?</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {ACTIONS.map((a) => {
              const usable = a.enabled && !!packageId && selectedReportReady;
              return (
                <button
                  key={a.type}
                  type="button"
                  disabled={!usable || starting}
                  onClick={() => usable && chooseAction(a.type)}
                  style={{
                    textAlign: 'left',
                    background: C.cardBg,
                    border: `1.5px solid ${C.border}`,
                    borderLeft: `3.5px solid ${a.accent}`,
                    borderRadius: 10,
                    padding: '14px 16px',
                    cursor: usable ? 'pointer' : 'default',
                    opacity: usable ? 1 : 0.45,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    font: 'inherit',
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: a.iconBg,
                      color: a.iconColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {starting && selectedPrompt === a.type ? <Spinner /> : a.icon}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 600,
                        color: C.textPrimary,
                        marginBottom: 3,
                      }}
                    >
                      {a.title}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 12,
                        color: C.textSecondary,
                        lineHeight: 1.45,
                      }}
                    >
                      {a.desc}
                    </span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 10,
                        color: C.textMuted,
                        marginTop: 8,
                      }}
                    >
                      {a.enabled ? a.footer : 'Coming soon'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {starting && (
          <div
            style={{
              marginTop: 20,
              color: C.textSecondary,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Spinner size={14} />
            Starting Addy…
          </div>
        )}
        {startError && (
          <div style={{ marginTop: 12, color: C.coralText, fontSize: 13 }}>
            {startError.message}
          </div>
        )}
      </div>
    );
  }

  // ── Chat: first output + refine ─────────────────────────────────────────
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: C.bodyBg,
      }}
    >
      <div
        style={{
          padding: '14px 24px',
          background: C.cardBg,
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={backToActions}
          aria-label="Back"
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.textSecondary,
            flexShrink: 0,
          }}
        >
          <Svg size={16}>
            <polyline points="15 18 9 12 15 6" />
          </Svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.textPrimary }}>
            {activeAction?.title || 'Ask Addy'}
          </div>
          <div style={{ fontSize: 12, color: C.textSecondary }}>
            {selectedReport?.name || 'Profile report'}
          </div>
        </div>
        {status === 'ready' && selectedPrompt && (
          <button
            type="button"
            onClick={regenerate}
            title="Generate a fresh result"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: C.cardBg,
              color: C.textPrimary,
              cursor: 'pointer',
              flexShrink: 0,
              marginRight: 8,
            }}
          >
            <Svg size={14}>
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </Svg>
            Regenerate
          </button>
        )}
        {activeAction && (
          <div
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: activeAction.badgeBg,
              color: activeAction.badgeColor,
            }}
          >
            {activeAction.badge}
          </div>
        )}
      </div>

      <div
        ref={bodyRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {status !== 'ready' && status !== 'failed' && (
          <AiRow>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: C.textSecondary,
              }}
            >
              <Spinner size={14} />
              Addy is analyzing your profile report…
            </span>
          </AiRow>
        )}

        {status === 'failed' && (
          <>
            <div style={{ color: C.coralText, fontSize: 14 }}>
              {conversation?.error || 'Addy could not generate this output.'}
            </div>
            <button
              type="button"
              onClick={backToActions}
              style={{
                alignSelf: 'flex-start',
                fontSize: 13,
                fontWeight: 500,
                padding: '8px 14px',
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: C.cardBg,
                cursor: 'pointer',
              }}
            >
              Try another action
            </button>
          </>
        )}

        {status === 'ready' &&
          messages.map((m, i) => {
            if (i === 0 && m.role === 'assistant') {
              // The first assistant message is the generated output. Render it as a
              // full-height card that is its own element in the chat flow. It needs
              // flexShrink: 0 because it's `overflow: hidden` (to clip its rounded
              // header/footer): inside the flex-column scroll area, an overflow-
              // hidden child can otherwise be shrunk below its content height and
              // clip the text. flexShrink: 0 keeps it at full height so the chat
              // area scrolls naturally and the whole output stays readable.
              return (
                <div
                  key={i}
                  style={{
                    background: C.cardBg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: 620,
                    alignSelf: 'flex-start',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    style={{
                      padding: '10px 16px',
                      background: C.bodyBg,
                      borderBottom: `1px solid ${C.borderLight}`,
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary }}>
                      {activeAction?.badge || 'Output'}
                    </span>
                  </div>
                  <div
                    style={{
                      padding: 16,
                      fontSize: 13,
                      color: C.textPrimary,
                      lineHeight: 1.65,
                    }}
                  >
                    <MarkdownView source={m.content || ' '} />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      borderTop: `1px solid ${C.borderLight}`,
                      background: C.bodyBg,
                    }}
                  >
                    {/* PDF outputs (exec summary) lead with Download; text
                        outputs lead with Copy. */}
                    {pdfOutput ? (
                      <button
                        type="button"
                        onClick={downloadOutput}
                        disabled={downloading}
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          padding: '6px 14px',
                          borderRadius: 8,
                          border: 'none',
                          background: C.teal,
                          color: '#fff',
                          cursor: downloading ? 'default' : 'pointer',
                          opacity: downloading ? 0.6 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        {downloading ? (
                          <Spinner size={13} />
                        ) : (
                          <Svg size={13}>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </Svg>
                        )}
                        {downloading ? 'Preparing…' : 'Download PDF'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={copyOutput}
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          padding: '6px 14px',
                          borderRadius: 8,
                          border: 'none',
                          background: C.teal,
                          color: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        <Svg size={13}>
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </Svg>
                        {copied ? 'Copied' : 'Copy to clipboard'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={regenerate}
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        padding: '6px 14px',
                        borderRadius: 8,
                        border: `1px solid ${C.border}`,
                        background: C.cardBg,
                        color: C.textPrimary,
                        cursor: 'pointer',
                      }}
                    >
                      Regenerate
                    </button>
                    {downloadError && (
                      <span style={{ color: C.coralText, fontSize: 12 }}>
                        {downloadError}
                      </span>
                    )}
                  </div>
                </div>
              );
            }
            return m.role === 'user' ? (
              <UserRow key={i} initial={userInitial}>
                {m.content}
              </UserRow>
            ) : (
              <AiRow key={i}>
                {m.content ? (
                  <MarkdownView source={m.content} fontSize={14} />
                ) : streaming && i === messages.length - 1 ? (
                  <Spinner size={14} />
                ) : (
                  ''
                )}
              </AiRow>
            );
          })}

        {chatError && <div style={{ color: C.coralText, fontSize: 13 }}>{chatError}</div>}
      </div>

      <div
        style={{
          padding: '16px 24px',
          background: C.cardBg,
          borderTop: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={status !== 'ready' || streaming}
          placeholder="Type your response…"
          style={{
            flex: 1,
            padding: '10px 16px',
            borderRadius: 10,
            border: `1.5px solid ${C.border}`,
            fontSize: 14,
            outline: 'none',
            background: C.bodyBg,
            color: C.textPrimary,
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          aria-label="Send"
          disabled={status !== 'ready' || streaming || !input.trim()}
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: C.teal,
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            opacity: status !== 'ready' || streaming || !input.trim() ? 0.5 : 1,
          }}
        >
          <Svg size={18}>
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </Svg>
        </button>
      </div>

      {/* Regenerate composer — collect optional changes, then re-run generation */}
      {refineOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              background: C.cardBg,
              borderRadius: 14,
              padding: 24,
              width: '100%',
              maxWidth: 520,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
              What would you like to change?
            </div>
            <div
              style={{
                fontSize: 13.5,
                color: C.textSecondary,
                lineHeight: 1.5,
                marginBottom: 16,
              }}
            >
              Describe any changes and Addy will regenerate{' '}
              {pdfOutput ? 'the PDF' : 'the output'}. Leave blank for a fresh
              version.
            </div>
            <textarea
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              autoFocus
              placeholder="e.g. Make the tone more formal and emphasize the travel segment."
              rows={4}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 10,
                border: `1.5px solid ${C.border}`,
                fontSize: 14,
                outline: 'none',
                background: C.bodyBg,
                color: C.textPrimary,
                font: 'inherit',
                resize: 'vertical',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                onClick={() => setRefineOpen(false)}
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  padding: '9px 16px',
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.textPrimary,
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRegenerate}
                disabled={starting}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '9px 18px',
                  borderRadius: 8,
                  border: 'none',
                  background: C.teal,
                  color: '#fff',
                  cursor: starting ? 'default' : 'pointer',
                  opacity: starting ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  font: 'inherit',
                }}
              >
                {starting ? (
                  <>
                    <Spinner size={13} />
                    Regenerating…
                  </>
                ) : (
                  'Regenerate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
